import { supabase } from '../lib/supabase';

/**
 * Helper: Normalize phone number to local French format (0XXXXXXXXX)
 */
const normalizePhone = (phone) => {
  if (!phone) return null;
  let cleaned = phone?.trim()?.replace(/[\s\-\.\(\)]/g, '');
  if (cleaned?.startsWith('0033')) cleaned = '+33' + cleaned?.slice(4);
  if (cleaned?.startsWith('+33')) cleaned = '0' + cleaned?.slice(3);
  if (cleaned?.length === 9 && /^[1-9]/?.test(cleaned)) cleaned = '0' + cleaned;
  return cleaned;
};

/**
 * Helper: Find or create a client from carton order data
 */
const findOrCreateMaterialClient = async (orderData, orderType = 'carton') => {
  try {
    const { data: clientId, error } = await supabase?.rpc('find_or_create_material_client', {
      p_name: orderData?.customerName || 'Client inconnu',
      p_phone: orderData?.customerPhone || null,
      p_email: orderData?.customerEmail || null,
      p_address: orderData?.customerAddress || null,
      p_city: orderData?.customerCity || null,
      p_postal_code: orderData?.customerPostalCode || null,
      p_source: 'barriques_cartons',
      p_order_type: orderType
    });

    if (error) {
      console.error('Error finding/creating material client (carton):', error);
      return null;
    }
    return clientId;
  } catch (err) {
    console.error('findOrCreateMaterialClient (carton) error:', err);
    return null;
  }
};

/**
 * Service for managing carton inventory, sales, suppliers, and truck loading
 */

export const cartonService = {
  // ===================== INVENTORY =====================
  async getCartonInventory() {
    try {
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.select('*, carton_suppliers(name)')
        ?.order('carton_type', { ascending: true });
      if (error) throw error;
      return data?.map(item => ({
        id: item?.id,
        cartonType: item?.carton_type,
        dimensions: item?.dimensions,
        description: item?.description,
        stockQuantity: item?.stock_quantity,
        minStockLevel: item?.min_stock_level,
        unitPriceBuy: item?.unit_price_buy,
        unitPriceSell: item?.unit_price_sell,
        supplierId: item?.supplier_id,
        supplierName: item?.carton_suppliers?.name,
        location: item?.location,
        notes: item?.notes,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching carton inventory:', error);
      throw error;
    }
  },

  async createCartonInventoryItem(itemData) {
    try {
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.insert([{
          carton_type: itemData?.cartonType,
          dimensions: itemData?.dimensions,
          description: itemData?.description,
          stock_quantity: itemData?.stockQuantity || 0,
          min_stock_level: itemData?.minStockLevel || 10,
          unit_price_buy: itemData?.unitPriceBuy || 0,
          unit_price_sell: itemData?.unitPriceSell || 0,
          supplier_id: itemData?.supplierId || null,
          location: itemData?.location || 'Entrepôt principal',
          notes: itemData?.notes
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating carton inventory item:', error);
      throw error;
    }
  },

  async updateCartonInventoryItem(id, itemData) {
    try {
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.update({
          carton_type: itemData?.cartonType,
          dimensions: itemData?.dimensions,
          description: itemData?.description,
          stock_quantity: itemData?.stockQuantity,
          min_stock_level: itemData?.minStockLevel,
          unit_price_buy: itemData?.unitPriceBuy,
          unit_price_sell: itemData?.unitPriceSell,
          supplier_id: itemData?.supplierId || null,
          location: itemData?.location,
          notes: itemData?.notes,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating carton inventory item:', error);
      throw error;
    }
  },

  async adjustCartonStock(id, adjustment, reason) {
    try {
      const { data: current, error: fetchError } = await supabase
        ?.from('carton_inventory')
        ?.select('stock_quantity')
        ?.eq('id', id)
        ?.single();
      if (fetchError) throw fetchError;
      const newQty = (current?.stock_quantity || 0) + adjustment;
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.update({ stock_quantity: newQty, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adjusting carton stock:', error);
      throw error;
    }
  },

  async deleteCartonInventoryItem(id) {
    try {
      const { error } = await supabase
        ?.from('carton_inventory')
        ?.delete()
        ?.eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting carton inventory item:', error);
      throw error;
    }
  },

  // ===================== SUPPLIERS =====================
  async getCartonSuppliers() {
    try {
      const { data, error } = await supabase
        ?.from('carton_suppliers')
        ?.select('*')
        ?.order('name', { ascending: true });
      if (error) throw error;
      return data?.map(s => ({
        id: s?.id,
        name: s?.name,
        contactName: s?.contact_name,
        phone: s?.phone,
        email: s?.email,
        address: s?.address,
        city: s?.city,
        country: s?.country,
        cartonTypes: s?.carton_types,
        unitPrice: s?.unit_price,
        minOrderQuantity: s?.min_order_quantity,
        leadTimeDays: s?.lead_time_days,
        notes: s?.notes,
        isActive: s?.is_active,
        supplierType: s?.supplier_type || 'carton',
        createdAt: s?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching carton suppliers:', error);
      throw error;
    }
  },

  async createCartonSupplier(supplierData) {
    try {
      const { data, error } = await supabase
        ?.from('carton_suppliers')
        ?.insert([{
          name: supplierData?.name,
          contact_name: supplierData?.contactName,
          phone: supplierData?.phone,
          email: supplierData?.email,
          address: supplierData?.address,
          city: supplierData?.city,
          country: supplierData?.country || 'France',
          carton_types: supplierData?.cartonTypes,
          unit_price: supplierData?.unitPrice,
          min_order_quantity: supplierData?.minOrderQuantity || 1,
          lead_time_days: supplierData?.leadTimeDays || 7,
          notes: supplierData?.notes,
          supplier_type: supplierData?.supplierType || 'carton',
          is_active: true
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating carton supplier:', error);
      throw error;
    }
  },

  async updateCartonSupplier(id, supplierData) {
    try {
      const { data, error } = await supabase
        ?.from('carton_suppliers')
        ?.update({
          name: supplierData?.name,
          contact_name: supplierData?.contactName,
          phone: supplierData?.phone,
          email: supplierData?.email,
          address: supplierData?.address,
          city: supplierData?.city,
          country: supplierData?.country,
          carton_types: supplierData?.cartonTypes,
          unit_price: supplierData?.unitPrice,
          min_order_quantity: supplierData?.minOrderQuantity,
          lead_time_days: supplierData?.leadTimeDays,
          notes: supplierData?.notes,
          supplier_type: supplierData?.supplierType || 'carton',
          is_active: supplierData?.isActive,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating carton supplier:', error);
      throw error;
    }
  },

  async deleteCartonSupplier(id) {
    try {
      const { error } = await supabase
        ?.from('carton_suppliers')
        ?.delete()
        ?.eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting carton supplier:', error);
      throw error;
    }
  },

  // ===================== SALES =====================
  async getCartonSales(filters = {}) {
    try {
      let query = supabase
        ?.from('carton_sales')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      if (filters?.paymentStatus) query = query?.eq('payment_status', filters?.paymentStatus);
      if (filters?.startDate) query = query?.gte('sale_date', filters?.startDate);
      if (filters?.endDate) query = query?.lte('sale_date', filters?.endDate);
      const { data, error } = await query;
      if (error) throw error;
      return data?.map(s => ({
        id: s?.id,
        reference: s?.reference,
        customerName: s?.customer_name,
        customerPhone: s?.customer_phone,
        customerEmail: s?.customer_email,
        cartonType: s?.carton_type,
        cartonInventoryId: s?.carton_inventory_id,
        quantity: s?.quantity,
        unitPrice: s?.unit_price,
        totalPrice: s?.total_price,
        paymentStatus: s?.payment_status,
        saleDate: s?.sale_date,
        notes: s?.notes,
        createdAt: s?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching carton sales:', error);
      throw error;
    }
  },

  async createCartonSale(saleData) {
    try {
      // Auto-create or link client
      let clientId = saleData?.clientId || null;
      if (!clientId && (saleData?.customerPhone || saleData?.customerName)) {
        clientId = await findOrCreateMaterialClient(saleData, 'carton');
      }

      const { data, error } = await supabase
        ?.from('carton_sales')
        ?.insert([{
          customer_name: saleData?.customerName,
          customer_phone: saleData?.customerPhone,
          customer_email: saleData?.customerEmail,
          carton_type: saleData?.cartonType,
          carton_inventory_id: saleData?.cartonInventoryId || null,
          quantity: saleData?.quantity,
          unit_price: saleData?.unitPrice,
          payment_status: saleData?.paymentStatus || 'pending',
          sale_date: saleData?.saleDate || new Date()?.toISOString()?.split('T')?.[0],
          notes: saleData?.notes,
          client_id: clientId
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating carton sale:', error);
      throw error;
    }
  },

  async updateCartonSalePayment(id, paymentStatus) {
    try {
      const { data, error } = await supabase
        ?.from('carton_sales')
        ?.update({ payment_status: paymentStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating carton sale payment:', error);
      throw error;
    }
  },

  async deleteCartonSale(id) {
    try {
      const { error } = await supabase
        ?.from('carton_sales')
        ?.delete()
        ?.eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting carton sale:', error);
      throw error;
    }
  },

  // ===================== TRUCK LOADING =====================
  async getTruckLoadingSessions(filters = {}) {
    try {
      let query = supabase
        ?.from('truck_loading_sessions')
        ?.select('*, truck_loading_items(*)')
        ?.order('loading_date', { ascending: false });
      if (filters?.status) query = query?.eq('status', filters?.status);
      if (filters?.startDate) query = query?.gte('loading_date', filters?.startDate);
      const { data, error } = await query;
      if (error) throw error;
      return data?.map(s => ({
        id: s?.id,
        reference: s?.reference,
        truckId: s?.truck_id,
        driverName: s?.driver_name,
        destination: s?.destination,
        loadingDate: s?.loading_date,
        status: s?.status,
        notes: s?.notes,
        items: s?.truck_loading_items || [],
        createdAt: s?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching truck loading sessions:', error);
      throw error;
    }
  },

  async createTruckLoadingSession(sessionData) {
    try {
      const { data, error } = await supabase
        ?.from('truck_loading_sessions')
        ?.insert([{
          truck_id: sessionData?.truckId,
          driver_name: sessionData?.driverName,
          destination: sessionData?.destination,
          loading_date: sessionData?.loadingDate || new Date()?.toISOString()?.split('T')?.[0],
          status: 'planned',
          notes: sessionData?.notes
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating truck loading session:', error);
      throw error;
    }
  },

  async updateTruckLoadingStatus(id, status) {
    try {
      const { data, error } = await supabase
        ?.from('truck_loading_sessions')
        ?.update({ status, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating truck loading status:', error);
      throw error;
    }
  },

  async addTruckLoadingItem(sessionId, itemData) {
    try {
      const { data, error } = await supabase
        ?.from('truck_loading_items')
        ?.insert([{
          session_id: sessionId,
          item_type: itemData?.itemType,
          barrel_type: itemData?.barrelType || null,
          carton_type: itemData?.cartonType || null,
          carton_inventory_id: itemData?.cartonInventoryId || null,
          quantity: itemData?.quantity,
          notes: itemData?.notes
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding truck loading item:', error);
      throw error;
    }
  },

  async removeTruckLoadingItem(itemId) {
    try {
      const { error } = await supabase
        ?.from('truck_loading_items')
        ?.delete()
        ?.eq('id', itemId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing truck loading item:', error);
      throw error;
    }
  },

  async deleteTruckLoadingSession(id) {
    try {
      const { error } = await supabase
        ?.from('truck_loading_sessions')
        ?.delete()
        ?.eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting truck loading session:', error);
      throw error;
    }
  },

  /**
   * Create a truck loading session with items and deduct stock in one operation
   * @param {Object} sessionData - Session info (truckId, driverName, destination, loadingDate, notes)
   * @param {Array} items - Array of { itemType, barrelType, cartonInventoryId, cartonType, quantity, notes }
   */
  async createTruckLoadingSessionWithItems(sessionData, items) {
    try {
      // 1. Create the session
      const { data: session, error: sessionError } = await supabase
        ?.from('truck_loading_sessions')
        ?.insert([{
          truck_id: sessionData?.truckId,
          driver_name: sessionData?.driverName,
          destination: sessionData?.destination,
          loading_date: sessionData?.loadingDate || new Date()?.toISOString()?.split('T')?.[0],
          status: 'planned',
          notes: sessionData?.notes
        }])
        ?.select()
        ?.single();
      if (sessionError) throw sessionError;

      // 2. Insert items
      if (items?.length > 0) {
        const itemRows = items?.map(item => ({
          session_id: session?.id,
          item_type: item?.itemType,
          barrel_type: item?.barrelType || null,
          carton_type: item?.cartonType || null,
          carton_inventory_id: item?.cartonInventoryId || null,
          quantity: item?.quantity,
          notes: item?.notes || null
        }));
        const { error: itemsError } = await supabase
          ?.from('truck_loading_items')
          ?.insert(itemRows);
        if (itemsError) throw itemsError;

        // 3. Deduct carton stock for carton items
        for (const item of items) {
          if (item?.itemType === 'carton' && item?.cartonInventoryId) {
            const { data: current, error: fetchErr } = await supabase
              ?.from('carton_inventory')
              ?.select('stock_quantity')
              ?.eq('id', item?.cartonInventoryId)
              ?.single();
            if (fetchErr) throw fetchErr;
            const newQty = Math.max(0, (current?.stock_quantity || 0) - item?.quantity);
            const { error: updateErr } = await supabase
              ?.from('carton_inventory')
              ?.update({ stock_quantity: newQty, updated_at: new Date()?.toISOString() })
              ?.eq('id', item?.cartonInventoryId);
            if (updateErr) throw updateErr;
          }
          // 4. Deduct barrel stock for barrel items
          if (item?.itemType === 'barrel' && item?.barrelType) {
            const { data: current, error: fetchErr } = await supabase
              ?.from('barrel_inventory')
              ?.select('stock_quantity')
              ?.eq('barrel_type', item?.barrelType)
              ?.single();
            if (fetchErr) throw fetchErr;
            const newQty = Math.max(0, (current?.stock_quantity || 0) - item?.quantity);
            const { error: updateErr } = await supabase
              ?.from('barrel_inventory')
              ?.update({ stock_quantity: newQty, updated_at: new Date()?.toISOString() })
              ?.eq('barrel_type', item?.barrelType);
            if (updateErr) throw updateErr;
          }
        }
      }

      return session;
    } catch (error) {
      console.error('Error creating truck loading session with items:', error);
      throw error;
    }
  }
};

export default cartonService;
